import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@/auth/AuthContext";
import { ApiError } from "@/api/http";
import { PigMark } from "@/components/Logo";

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo ?? "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needTotp, setNeedTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password, needTotp ? totp : undefined);
      navigate(returnTo, { replace: true });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Sign in failed.";
      if (/totp|mfa|2fa/i.test(msg)) {
        setNeedTotp(true);
        setError("Enter your authenticator code to continue.");
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  const inputCls =
    "mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-ink placeholder-ink-mute focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20";

  return (
    <div className="mx-auto max-w-sm">
      <div className="rounded-2xl border border-line bg-surface p-7 shadow-card">
        <div className="flex flex-col items-center text-center">
          <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand-soft">
            <PigMark size={30} />
          </div>
          <h1 className="mt-3 text-xl font-extrabold tracking-tight text-ink">Sign in</h1>
          <p className="mt-1 text-sm text-ink-soft">Use your Piggy account.</p>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-ink-soft">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-soft">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className={inputCls}
            />
          </div>
          {needTotp && (
            <div>
              <label className="block text-sm font-medium text-ink-soft">Authenticator code</label>
              <input value={totp} onChange={(e) => setTotp(e.target.value)} inputMode="numeric" className={`${inputCls} font-mono`} />
            </div>
          )}
          {error && <p className="text-sm text-down">{error}</p>}
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-lg bg-brand px-4 py-2.5 font-semibold text-white shadow-sm transition-colors hover:bg-brand-strong disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
      <p className="mt-4 text-center text-sm text-ink-soft">
        No account?{" "}
        <Link to="/register" className="font-semibold text-brand-strong">
          Create one
        </Link>
      </p>
    </div>
  );
}
